import { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';

const RangeFilter = ({ 
  title, 
  isOpen, 
  toggleFilter, 
  min, 
  max, 
  currentMin,
  currentMax,
  onRangeChange,
  unit = '' 
}) => {
  // Default values if min/max are null
  const defaultMin = min !== null ? min : 0;
  const defaultMax = max !== null ? max : 100;
  
  // Form state for the input fields
  const [minValue, setMinValue] = useState(currentMin !== null ? currentMin : defaultMin);
  const [maxValue, setMaxValue] = useState(currentMax !== null ? currentMax : defaultMax);
  
  // Update form values when props change
  useEffect(() => {
    if (min !== null && currentMin === null) setMinValue(min);
    if (max !== null && currentMax === null) setMaxValue(max);
    
    if (currentMin !== null) setMinValue(currentMin);
    if (currentMax !== null) setMaxValue(currentMax);
  }, [min, max, currentMin, currentMax]);

  // Keep a stable debounced handler and recreate only when callback changes.
  const debouncedRangeChange = useMemo(
    () => debounce((nextMin, nextMax) => {
      onRangeChange({ min: nextMin, max: nextMax });
    }, 500),
    [onRangeChange]
  );

  // Handle input changes
  const handleMinChange = (e) => {
    const value = e.target.value === '' ? null : parseFloat(e.target.value);
    setMinValue(value);
    
    // Only update if the value is valid
    if (value === null || !isNaN(value)) {
      debouncedRangeChange(value, maxValue);
    }
  };
  
  const handleMaxChange = (e) => {
    const value = e.target.value === '' ? null : parseFloat(e.target.value);
    setMaxValue(value);
    
    // Only update if the value is valid
    if (value === null || !isNaN(value)) {
      debouncedRangeChange(minValue, value);
    }
  };

  // Reset range to default values
  const handleReset = () => {
    setMinValue(defaultMin);
    setMaxValue(defaultMax);
    onRangeChange({ min: null, max: null });
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      debouncedRangeChange.cancel();
    };
  }, [debouncedRangeChange]);

  // Check if current range is different from default
  const isFiltered = 
    (currentMin !== null && currentMin !== defaultMin) || 
    (currentMax !== null && currentMax !== defaultMax);

  return (
    <div className="mb-4 filter-section">
      <button 
        className="flex w-full justify-between p-0 h-auto mb-2 hover:bg-transparent filter-toggle"
        onClick={toggleFilter}
      >
        <h3 className="font-medium text-cream tracking-wider uppercase text-sm">
          {title} {isFiltered && '(filtered)'}
        </h3>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="20" height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round" 
          className={`h-5 w-5 transition-transform text-purple transform ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>
      <div className={`filter-content space-y-4 pl-1 pt-2 ${!isOpen ? 'hidden' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="w-[45%]">
            <label htmlFor={`min-${title.toLowerCase().replace(' ', '-')}`} className="text-xs mb-1 block tracking-wide text-cream/80">
              Min {unit && `(${unit})`}
            </label>
            <input 
              id={`min-${title.toLowerCase().replace(' ', '-')}`}
              type="number"
              value={minValue !== null ? minValue : ''}
              onChange={handleMinChange}
              className="w-full h-8 rounded-md border border-purple/30 bg-dark/50 px-3 py-1 text-cream" 
              placeholder={defaultMin}
            />
          </div>
          <div className="text-cream/50">-</div>
          <div className="w-[45%]">
            <label htmlFor={`max-${title.toLowerCase().replace(' ', '-')}`} className="text-xs mb-1 block tracking-wide text-cream/80">
              Max {unit && `(${unit})`}
            </label>
            <input 
              id={`max-${title.toLowerCase().replace(' ', '-')}`}
              type="number"
              value={maxValue !== null ? maxValue : ''}
              onChange={handleMaxChange}
              className="w-full h-8 rounded-md border border-purple/30 bg-dark/50 px-3 py-1 text-cream" 
              placeholder={defaultMax}
            />
          </div>
        </div>
        
        {/* Reset button for range filter */}
        {isFiltered && (
          <button 
            onClick={handleReset}
            className="text-xs text-skyblue p-0 h-auto tracking-wide mt-2"
          >
            RESET RANGE
          </button>
        )}
      </div>
    </div>
  );
};

export default RangeFilter;
